import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { AuthContext } from '../context/authContext';
import karaokeService from '../services/karaoke.service';
import SongLibrary from '../components/karaoke/SongLibrary';
import SongStage from '../components/karaoke/SongStage';
import TakeGallery from '../components/karaoke/TakeGallery';
import '../components/karaoke/karaoke.css';

/**
 * GeetSuhane Karaoke.
 *
 * Hear the original in full (the master streams from YouTube and is never
 * copied into our bucket), sing over the instrumental, mix on your own device,
 * and leave with an MP4 for your own channel.
 */

const TABS = { LIBRARY: 'library', SING: 'sing', COVERS: 'covers', MINE: 'mine' };

export default function Karaoke() {
  const { currentUser } = useContext(AuthContext);

  const [tab, setTab] = useState(TABS.LIBRARY);
  const [songs, setSongs] = useState([]);
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedCount, setSavedCount] = useState(0);

  const loadSongs = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const data = await karaokeService.listSongs({ q });
      setSongs(data.songs || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadSongs(); }, [loadSongs]);

  const pickSong = async (picked) => {
    setTab(TABS.SING);
    setSong(picked);
    try {
      const data = await karaokeService.getSong(picked.id);
      setSong(data.song);
    } catch (e) {
      setError(e.message);
    }
  };

  // Bump a counter so "My takes" refetches after a save instead of showing a
  // stale list the singer has to reload to fix.
  const onSaved = () => setSavedCount((n) => n + 1);

  return (
    <div className="main-content right-chat-active">
      <div className="middle-sidebar-bottom">
        <div className="middle-sidebar-left">
          <div className="gs-karaoke">
            <Helmet>
              <title>Karaoke - Sing GeetSuhane Originals</title>
              <meta name="description" content="Hear the original, sing over the backing track, and take away a video for your own channel." />
            </Helmet>

            <header className="gs-karaoke__head">
              <h2>Karaoke</h2>
              <p>
                Hear the original in full, then sing it yourself. Your take is mixed on
                your own phone and you leave with a video ready for your channel.
              </p>
            </header>

            <nav className="gs-karaoke__tabs">
              <button type="button" className={tab === TABS.LIBRARY ? 'is-active' : ''}
                onClick={() => setTab(TABS.LIBRARY)}>Songs</button>
              <button type="button" className={tab === TABS.SING ? 'is-active' : ''}
                onClick={() => song && setTab(TABS.SING)} disabled={!song}>
                {song ? song.title : 'Sing'}
              </button>
              <button type="button" className={tab === TABS.COVERS ? 'is-active' : ''}
                onClick={() => setTab(TABS.COVERS)}>Covers</button>
              {currentUser && (
                <button type="button" className={tab === TABS.MINE ? 'is-active' : ''}
                  onClick={() => setTab(TABS.MINE)}>Mine</button>
              )}
            </nav>

            {error && <div className="gs-karaoke__error">{error}</div>}

            {tab === TABS.LIBRARY && (
              <SongLibrary songs={songs} loading={loading} onPick={pickSong} onSearch={loadSongs} />
            )}

            {tab === TABS.SING && song && (
              <SongStage song={song} currentUser={currentUser} onSaved={onSaved} />
            )}

            {tab === TABS.COVERS && (
              <TakeGallery songId={song ? song.id : undefined} currentUser={currentUser} />
            )}

            {tab === TABS.MINE && currentUser && (
              <TakeGallery key={savedCount} mine currentUser={currentUser} />
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
